import _ from 'lodash';

import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TeamService } from 'src/app/shared/services/team.service';
import { LineupErrorStateMatcher } from 'src/app/shared/services/error-state-matcher.service';
import { Team } from 'src/app/shared/models/team/team';
import { TeamWrite } from 'src/app/shared/models/team/teamWrite';
import { PositionWrite } from 'src/app/shared/models/team/positionWrite';
import { UniqueValidator } from 'src/app/shared/validators/unique.validator';
import { NotEmptyValidator } from 'src/app/shared/validators/not-empty.validator';
import { NoPipeValidator } from 'src/app/shared/validators/no-pipe.validator';
import {
  DateLessThanValidator,
  MustHaveStartDateIfEndDateValidator
} from 'src/app/shared/validators/date.validator';
import { AttributeService } from 'src/app/shared/services/attribute.service';
import { AttributeEntityType } from 'src/app/shared/models/attributes/attributeEntityType';
import { AttributesEditorDataSource } from 'src/shared-app-portal/components/attributes-editor/attributesEditorDataSource';
import { AttributeEditorMode } from 'src/app/shared/models/attributes/attributeEditorMode';

@Component({
  selector: 'app-team-add-details',
  templateUrl: 'team-add-details.component.html',
  styleUrls: ['team-add-details.component.scss'],
  standalone: false
})
export class TeamAddDetailsComponent implements OnInit, OnDestroy {
  private readonly positionTitleRequiredErrorMessage = 'Position title is required';
  private attrSub;
  private teamSub;

  attributesDataSource: AttributesEditorDataSource = new AttributesEditorDataSource();
  teamForm: UntypedFormGroup;
  dateFormGroup: UntypedFormGroup;
  errorMessage: any;
  team: Team;
  teamWrite: TeamWrite;
  positionWrite: PositionWrite;
  matcher = new LineupErrorStateMatcher();

  get isValid(): boolean {
    return this.teamForm && this.teamForm.valid && this.attributesDataSource.isValid;
  }

  get teamDetailsMode(): AttributeEditorMode {
    return AttributeEditorMode.TeamDetails;
  }

  constructor(
    private attributeService: AttributeService,
    private teamService: TeamService,
    private formBuilder: UntypedFormBuilder
  ) {}

  buildTeamForm(teamNames: Array<string>) {
    this.dateFormGroup = this.formBuilder.group({
      startDate: null,
      endDate: null
    });

    this.dateFormGroup.setValidators([
      DateLessThanValidator('startDate', 'endDate'),
      MustHaveStartDateIfEndDateValidator('startDate', 'endDate')
    ]);

    this.teamForm = this.formBuilder.group({
      name: [
        '',
        [
          Validators.minLength(4),
          Validators.maxLength(255),
          UniqueValidator(teamNames, true, 'Team name must be unique'),
          NotEmptyValidator('Team name is required')
        ]
      ],
      publicName: ['', [Validators.minLength(4), Validators.maxLength(255)]],
      dateGroup: this.dateFormGroup,
      positions: this.formBuilder.array(this.initPositions([]))
    });
  }

  ngOnInit() {
    this.attrSub = this.attributeService.attributes.subscribe((attributeDefinitions) => {
      if (!attributeDefinitions || attributeDefinitions.length < 1) {
        return;
      }
      let filtered = _.filter(
        attributeDefinitions,
        (attr) =>
          attr.entityType == AttributeEntityType.Team &&
          attr.showOnCreate == true &&
          !attr.isArchived
      );

      this.attributesDataSource.load([], filtered);
    });
    this.teamSub = this.teamService.teams.subscribe(
      (teams) => {
        let teamNames = _.map(teams, 'name');
        this.buildTeamForm(teamNames);
      },
      (error) => (this.errorMessage = <any>error)
    );
  }

  ngOnDestroy(): void {
    if (this.teamSub) this.teamSub.unsubscribe();
    if (this.attrSub) this.attrSub.unsubscribe();
  }

  get positionControlsArray(): UntypedFormArray {
    return <UntypedFormArray>this.teamForm.get('positions');
  }

  get positionFormGroups(): UntypedFormGroup[] {
    return this.positionControlsArray.controls as UntypedFormGroup[];
  }

  addPosition() {
    this.positionControlsArray.push(this.initPosition(this.teamForm.value.positions.length + 1));
  }

  removePosition(i: number) {
    if (this.positionControlsArray.length > 1) {
      this.positionControlsArray.removeAt(i);
    }
  }

  initPositions(positions: PositionWrite[]): UntypedFormGroup[] {
    const groups = [];

    if (!positions || (positions && positions.length === 0)) {
      groups.push(this.initPosition(1));
    } else {
      for (var index = 0; index < positions.length; ++index) {
        var p = positions[index] as PositionWrite;

        const pg = this.formBuilder.group({
          title: [
            p.title,
            [NotEmptyValidator(this.positionTitleRequiredErrorMessage), NoPipeValidator()]
          ],
          count: [p.count, [Validators.min(0), Validators.required]],
          index: [index],
          id: [p.id]
        });
        groups.push(pg);
      }
    }

    return groups;
  }

  saveNewTeam() {
    this.assignFormValuesToTeam();
    this.teamService.createTeam(this.teamWrite);
  }

  assignFormValuesToTeam(): void {
    const teamFormModel = this.teamForm.value;
    const dateFormModel = this.dateFormGroup.value;

    var newName = teamFormModel.name;
    var newPublicName = teamFormModel.publicName;
    var newPositions = teamFormModel.positions;
    var newStartDate = dateFormModel.startDate ? new Date(dateFormModel.startDate) : null;
    var newEndDate = dateFormModel.endDate ? new Date(dateFormModel.endDate) : null;
    var newAttributes = this.attributesDataSource.hasAttributes
      ? this.attributesDataSource.asAttributeWrites()
      : [];

    this.teamWrite = new TeamWrite(
      null,
      newName,
      newPublicName,
      newPositions,
      newAttributes,
      newStartDate,
      newEndDate
    );
  }

  initPosition(id) {
    return this.formBuilder.group({
      title: ['', [NotEmptyValidator(this.positionTitleRequiredErrorMessage), NoPipeValidator()]],
      count: [1, [Validators.min(1), Validators.required]],
      index: [id]
    });
  }
}
